// UserInfoAdmin.js
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import useClientSize from '../hook/useClientSize';
import UserInfoList from './UserInfoList';
import UserInfoInput from './UserInfoInput';
import ConfirmDialog from '../message/ConfirmDialog';
import ResponseAlert from '../message/ResponseAlert';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import {
    LIST_USER_INFO, GET_USER_INFO, ADD_USER_INFO, EDIT_USER_INFO, REMOVE_USER_INFO,
    UserInfoDictionary,
    SET_USER_INFO_STATE
} from './UserInfoGql';
import { LOG_IN_AS } from '../session/SessionGql';
import { AppObject, AppNumber, CvoMsg, AppWord, AppPalette } from '../../model/AppConst';
import ValueUtil from '../../model/ValueUtil';
import { userInfoRepo, NoUser } from '../../model/CvoModel';
import UserSessionInfo from '../common/UserSessionInfo';
import { useSnackbar } from 'notistack';
import { PromptContext } from '../hook/appPrompters';

const UserInfoAdminContainer = styled(Box)({flexGrow:1});

const ErrorTitle = UserInfoDictionary.errorTitle;
const EditFields = ValueUtil.getFieldsToSubmit(UserInfoDictionary.inputMap, true);


/**
 * 사용자 정보 컨트롤러.
 */
export default function UserInfoAdmin({
    maxWidth,
    maxHeight,
    custInfo,
}) {
	const [userInfoSelected, setUserInfoSelected] = useState(null);
    const [editorState, setEditorState] = useState(null);
    const [promptRemove, setPromptRemove] = useState(null);
    const [responseAlert, setResponseAlert] = useState(null);
    const { openConfirmDialog } = useContext(PromptContext);
    const { enqueueSnackbar } = useSnackbar();
    const sessionInfo = useReactiveVar(userInfoRepo);
    const clientSize = useClientSize();
    const loadedRef = useRef(0);

	const isAdmin = ValueUtil.isEtrace(sessionInfo);

    // ##### Call GraphQL to get List #####
    const [getUserInfoList, responseList] = useLazyQuery(LIST_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: "사용자 목록 조회 오류"})}
    });
    const [getUserInfoItemToEdit, responseItemToEdit] = useLazyQuery(GET_USER_INFO,
    {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Get})}
    });
    const [getUserInfoItemToShow, responseItemToShow] = useLazyQuery(GET_USER_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetItem(data, option, true)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Get})}
    });
    
    // ##### GraphQL Mutation ###
    const [addUserInfo, responseAdd] = useMutation( ADD_USER_INFO, {
        onCompleted: (data, option) => onCompleteAdd(data, option), 
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Add})}
    } );
    const [editUserInfo, responseEdit] = useMutation( EDIT_USER_INFO, {
        onCompleted: (data, option) => onCompleteEdit(data, option),
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Edit})}
    } );
    const [setStateUserInfo, responseSetState] = useMutation( SET_USER_INFO_STATE, {
        onCompleted: (data, option) => onCompleteSetState(data, option),
        onError: (error) => {setResponseAlert({open:true, error: error, title: "사용자 상태 변경 오류"})}
    });
    const [removeUserInfo, responseRemove] = useMutation( REMOVE_USER_INFO, {
        onCompleted: (data, option) => onCompleteRemove(data, option),
        onError: (error) => {setResponseAlert({open:true, error: error, title: ErrorTitle.Remove})}
    });
    const [logInAsUser, responseUserAs] = useLazyQuery(LOG_IN_AS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteLoginAsUser(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: "타 사용자로 가장하기 오류"})}
    });

    useEffect(() => {
        if(custInfo) getUserInfoList({variables:{custId:custInfo.custId}});
        else getUserInfoList();
    }, [custInfo, getUserInfoList]);


    const getUserInfoListOfCust = () => {
        if(custInfo) getUserInfoList({variables:{custId:custInfo.custId}});
        else getUserInfoList();
    };

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.userInfoList) {
            loadedRef.current = (new Date()).getTime() / 1000;

            if(userInfoSelected) {
                for(const item of data.userInfoList) {
                    if(item.userId===userInfoSelected.userId) {
                        setUserInfoSelected(item);
                        return;
                    }
                }
            }
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.userInfoAdd.ok) {
            setEditorState(null);
            getUserInfoListOfCust();
        }
        else setResponseAlert({open:true, resultData: data.userInfoAdd, title: ErrorTitle.Add});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.userInfoEdit.ok) {
            setEditorState(null);
            getUserInfoListOfCust();
        }
        else setResponseAlert({open:true, resultData: data.userInfoEdit, title: ErrorTitle.Edit});
    };

    const onCompleteSetState = (data, clientOption) => {
        if(data.userInfoSetState.ok) {
            getUserInfoListOfCust();
        }
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.userInfoRemove.ok) getUserInfoListOfCust();
        else setResponseAlert({open:true, resultData: data.userInfoRemove, title: ErrorTitle.Remove});
    };

    const onCompleteGetItem = (data, option, toShow) => {
        if(data.userInfoItem) {
            const item = {};
            for(const field of EditFields) item[field] = data.userInfoItem[field];
            setEditorState({item: item, readOnly: toShow ? true : false});
        }
    };

    // admin
    const onCompleteLoginAsUser = (data, option) => {
        if(data.loginAs) {
            if(data.loginAs.userAs.msg===CvoMsg.OK) {
                // userInfoRepo({...data.loginAs, signedIn: true, sessionChecked: true});
                userInfoRepo(new UserSessionInfo(true, true, data.loginAs));
                // inform AppMain.js
                enqueueSnackbar("타 사용자로 로그인 성공: " + data.loginAs.userAs.userName, {variant:'info', autoHideDuration:2000});
            }
        }
    };

    // +++++++ UI callbacks ++++++++
    const onRequestNew = () => {
        if(responseAdd) responseAdd.reset();
        setEditorState({item: undefined}); // input component will set a default data.
    };

    const onRequestEdit = (item) => {
        if(responseEdit) responseEdit.reset();
        getUserInfoItemToEdit({variables: {userId: item.userId}});
        // setEditorState({item: item});
    };

    const onRequestSetState = async (param) => {
        const to = param.userStateCd === UserInfoDictionary.userState.STOPPED ? "사용중지" :
            param.userStateCd === UserInfoDictionary.userState.LEFT ? "해지" : "정상";
        const ok = await openConfirmDialog({
            title: '사용자 상태 변경',
            message: `사용자 ${param.userId}님의 상태를 ${to} 상태로 변경하시겠습니까?`,
            option: { yesLabel: '변경', noLabel: '취소' }
        });

        if(ok) {
            if(responseSetState) responseSetState.reset();
            setStateUserInfo({variables: {userInfo: param}});
        }
    };

    const onRequestReload = () => {
        const diff = (new Date()).getTime() / 1000 - loadedRef.current;
        if(diff < 5) {
            enqueueSnackbar(AppWord.NO_RELOAD_IN_5_SEC,
                {variant:AppPalette.WarnColor, autoHideDuration:1500});
        }
        else getUserInfoListOfCust();
    };

    const onRequestAsUser = (user) => {
        if(!isAdmin) return;
        if(sessionInfo.user.classNo >= user.classNo) {
            enqueueSnackbar("권한이 큰 사용자로 가장할 수 없습니다.", {variant:AppPalette.WarnColor, autoHideDuration:2000});
            return;
        }
        if(user.custId===AppWord.ETRACE_CUST_ID) {
            enqueueSnackbar("이트레이스 사용자 대상으로 허용되지 않는 기능입니다.", {variant:AppPalette.WarnColor, autoHideDuration:2000});
            return;
        }
        logInAsUser({variables: {userId: user.userId}});
    };

    const onRequestView = (item) => {
        // if(responseItemToShow) responseItemToShow.reset();
        getUserInfoItemToShow({variables: {userId: item.userId}});
    };

    const onRequestRemove = (item) => {

        setPromptRemove({
            data: item,
            title: '사용자 정보 삭제',
            messages: [
                '사용자 ' + item.userName + '(' + item.userId + ')님을 삭제하시겠습니까?',
                '해당 사용자의 정보 및 부수하는 정보는 즉시, 완전히 삭제됩니다.',
                '삭제된 정보는 복구할 수 없습니다',
                '정보 삭제를 진행하시겠습니까?'
            ],
            callback: (data) => {
                setPromptRemove(null);
                if(data) {
                    if(responseRemove) responseRemove.reset();
                    const param = {variables:{userInfo:{userId: data.userId, custId: data.custId}}};
                    removeUserInfo(param);
                }
            }
        });
    };

    // Handler - Submit for mutation fired by UserInfoInput component.
    const onClickMutate = (item, isEdit) => {
        // item.custId = ''; // testing error callback.
        const param = {variables: {userInfo: ValueUtil.refineToSubmit(item)}};
        if(isEdit) editUserInfo(param);
        else addUserInfo(param);
    };

    const onCloseEditor = () => {
        setEditorState(null);
    };

    // ---------------------------- Result handling ----------------------------
    if(ValueUtil.hasAnyAuthError(
        responseList, responseAdd, responseEdit, responseRemove, responseUserAs, responseSetState,
        responseItemToEdit, responseItemToShow
    )) userInfoRepo(NoUser);

    // ---------------------------- Render Components ----------------------------
    const renderPromptRemoveBox = () => {
        const prompt = promptRemove ? promptRemove : {};
        return (
            <ConfirmDialog
                open={Boolean(promptRemove)}
                prompt={prompt}
                onClose={prompt.callback}
            />
        );
    };

    const renderEditor = () => {
        const es = editorState ? editorState : {item: undefined};

        return(
            es.item
            ?
            <UserInfoInput
                open={Boolean(editorState)}
                isEdit={true}
                item={es.item}
                responseSaving={responseEdit}
                onClickMutate={onClickMutate}
                onClose={onCloseEditor}
                readOnly={es.readOnly}
                />
            :
            <UserInfoInput
                open={Boolean(editorState)}
                isEdit={false}
                custId={custInfo ? custInfo.custId : ''}
                responseSaving={responseAdd}
                onClickMutate={onClickMutate}
                onClose={onCloseEditor}
                />
        );
    };

    const height = maxHeight || clientSize.dataAreaHeight;
    const width = maxWidth || clientSize.dataAreaWidth;

    return (
        <UserInfoAdminContainer>
            <UserInfoList
                maxHeight={height}
                maxWidth={width - AppNumber.dataContainerPadding - 2}
                responseList={responseList}
                selected={userInfoSelected}
                onClickItem={setUserInfoSelected}
                onRequestAdd={onRequestNew}
                onRequestEdit={onRequestEdit}
                onRequestRemove={onRequestRemove}
                onRequestAsIs={onRequestAsUser}
                onRequestReload={onRequestReload}
                onRequestView={onRequestView}
                onRequestSetState={onRequestSetState}
            />
            {renderEditor()}
            {renderPromptRemoveBox()}
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </UserInfoAdminContainer>
    )
}
